import { useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { X, Loader2, ShieldCheck, ScanLine, Camera, Sparkles, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Structured UK dealership VHC — matches the depth of a Toyota / Lexus paper
// sheet. Categories expand/collapse; specific items get numeric measurement
// inputs (tyre tread mm, brake-pad thickness mm) that AUTO-calculate % worn
// and AUTO-suggest the traffic-light grade so the tech doesn't have to think
// twice. Grade thresholds:  <50%=Good  ·  50-79%=Advisory  ·  ≥80%=Attention.
//
// Wear formulas (Steve's spec):
//   Tyre:  8 mm = 0% worn,  1.6 mm = 100% worn  →  (8 - mm) / (8 - 1.6) × 100
//   Pad:   12 mm = 0% worn,  2 mm = 100% worn   →  (12 - mm) / (12 - 2) × 100
const CATEGORIES = [
  {
    name: "Brakes",
    items: [
      { key: "brake_pads_front_ns", label: "NSF brake pad", measure: "pad" },
      { key: "brake_pads_front_os", label: "OSF brake pad", measure: "pad" },
      { key: "brake_pads_rear_ns",  label: "NSR brake pad", measure: "pad" },
      { key: "brake_pads_rear_os",  label: "OSR brake pad", measure: "pad" },
      { key: "brake_discs_front",   label: "Front brake discs" },
      { key: "brake_discs_rear",    label: "Rear brake discs" },
      { key: "brake_callipers",     label: "Callipers (seizure, corrosion)" },
      { key: "brake_hoses",         label: "Brake hoses & pipes" },
      { key: "brake_fluid",         label: "Brake fluid level & condition" },
      { key: "handbrake",           label: "Handbrake / EPB operation" },
    ],
  },
  {
    name: "Tyres & Wheels",
    items: [
      { key: "tyre_nsf",       label: "NSF tyre", measure: "tyre" },
      { key: "tyre_osf",       label: "OSF tyre", measure: "tyre" },
      { key: "tyre_nsr",       label: "NSR tyre", measure: "tyre" },
      { key: "tyre_osr",       label: "OSR tyre", measure: "tyre" },
      { key: "tyre_sidewalls", label: "Sidewalls, bulges & cuts" },
      { key: "tyre_pressures", label: "Pressures (all four + spare)" },
      { key: "wheel_alignment",label: "Wheel alignment (visual)" },
      { key: "spare_wheel",    label: "Spare wheel & tools" },
    ],
  },
  {
    name: "Steering & Suspension",
    items: [
      { key: "shocks_front",       label: "Front shock absorbers" },
      { key: "shocks_rear",        label: "Rear shock absorbers" },
      { key: "springs_front",      label: "Front springs" },
      { key: "springs_rear",       label: "Rear springs" },
      { key: "top_mounts",         label: "Strut top mounts" },
      { key: "wishbone_bushes",    label: "Wishbone bushes" },
      { key: "arb_drop_links",     label: "ARB drop links" },
      { key: "ball_joints",        label: "Ball joints" },
      { key: "track_rod_ends",     label: "Track rod ends" },
      { key: "subframe_bushes",    label: "Subframe bushes" },
      { key: "steering_rack",      label: "Steering rack & gaiters" },
      { key: "steering_column",    label: "Steering column play" },
    ],
  },
  {
    name: "Lights & Electrics",
    items: [
      { key: "headlights",   label: "Headlights (main + dip)" },
      { key: "sidelights",   label: "Sidelights & indicators" },
      { key: "brake_lights", label: "Brake lights & reverse" },
      { key: "battery",      label: "Battery condition test" },
    ],
  },
  {
    name: "Under Bonnet",
    items: [
      { key: "oil_level",  label: "Engine oil level & condition" },
      { key: "coolant",    label: "Coolant level & strength" },
      { key: "aux_belt",   label: "Auxiliary drive belt" },
      { key: "air_filter", label: "Air filter" },
    ],
  },
  {
    name: "Wipers & Glass",
    items: [
      { key: "wipers_front", label: "Front wiper blades" },
      { key: "wipers_rear",  label: "Rear wiper blade" },
      { key: "windscreen",   label: "Windscreen chips & cracks" },
    ],
  },
];

// Traffic-light values stay as green/amber/red on the wire — everything
// (commission engine, analytics, customer render) keys off those.
const GRADES = [
  { v: "green", short: "Good",
    active: "bg-success text-success-foreground border-success ring-success/40" },
  { v: "amber", short: "Advisory",
    active: "bg-warning text-warning-foreground border-warning ring-warning/40" },
  { v: "red",   short: "Attention",
    active: "bg-destructive text-destructive-foreground border-destructive ring-destructive/40" },
];

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

function wearFromMeasurement(kind, mm) {
  const n = parseFloat(mm);
  if (!isFinite(n) || n < 0) return null;
  if (kind === "tyre") return Math.round(clamp((8 - n) / (8 - 1.6) * 100, 0, 100));
  if (kind === "pad")  return Math.round(clamp((12 - n) / (12 - 2) * 100, 0, 100));
  return null;
}

function gradeFromWear(wearPct) {
  if (wearPct == null) return null;
  if (wearPct >= 80) return "red";
  if (wearPct >= 50) return "amber";
  return "green";
}

export default function VhcChecklistModal({ jobId, open, onClose, onSaved, vehicle }) {
  // grades[key] = { grade, note, price, mm, wear, photos:[{path,url}], aiBusy }
  const [grades, setGrades] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(() =>
    Object.fromEntries(CATEGORIES.map((c) => [c.name, true]))
  );
  const fileRefs = useRef({});

  const summary = useMemo(() => {
    let g = 0, a = 0, r = 0;
    Object.values(grades).forEach((v) => {
      if (v?.grade === "green") g++;
      else if (v?.grade === "amber") a++;
      else if (v?.grade === "red") r++;
    });
    return { g, a, r, done: g + a + r };
  }, [grades]);

  const totalItems = CATEGORIES.reduce((n, c) => n + c.items.length, 0);

  const setField = (key, patch) => {
    setGrades((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };
  const setGrade = (key, grade) => setField(key, { grade });

  const onMeasurementChange = (key, kind, mm) => {
    const wear = wearFromMeasurement(kind, mm);
    const autoGrade = gradeFromWear(wear);
    setGrades((prev) => {
      const cur = prev[key] || {};
      // Only auto-set grade if the tech hasn't manually overridden yet, OR the
      // current grade came from a previous auto-set (`autoGraded` flag).
      const canAuto = !cur.grade || cur.autoGraded;
      return {
        ...prev,
        [key]: {
          ...cur,
          mm,
          wear,
          grade: canAuto && autoGrade ? autoGrade : cur.grade,
          autoGraded: canAuto ? true : cur.autoGraded,
        },
      };
    });
  };

  const manualSetGrade = (key, grade) => {
    setGrades((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), grade, autoGraded: false },
    }));
  };

  const reset = () => { setGrades({}); setSaving(false); };
  const close = () => { reset(); onClose?.(); };
  const toggleCategory = (name) => setExpanded((e) => ({ ...e, [name]: !e[name] }));

  const openCamera = (key) => {
    const el = fileRefs.current[key];
    if (el) { el.value = ""; el.click(); }
  };

  const onPhotoChosen = async (key, e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post(`/jobs/${jobId}/vhc-photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const localPreview = URL.createObjectURL(f);
        setGrades((prev) => {
          const g = prev[key] || {};
          const photos = [...(g.photos || []), { path: r.data.path, url: localPreview }];
          return { ...prev, [key]: { ...g, photos } };
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't upload photo — try again.");
      }
    }
  };

  const removePhoto = (key, idx) => {
    setGrades((prev) => {
      const g = prev[key] || {};
      const photos = (g.photos || []).filter((_, i) => i !== idx);
      return { ...prev, [key]: { ...g, photos } };
    });
  };

  const aiWrite = async (key, itemLabel) => {
    const g = grades[key] || {};
    if (!g.grade || g.grade === "green") return;
    setField(key, { aiBusy: true });
    // Enrich the tech note with measurement context so Claude writes with the
    // actual mm value where present ("2.5mm of a legal min 1.5mm").
    let contextNote = g.note || "";
    if (g.mm && g.wear != null) {
      contextNote = `${contextNote ? contextNote + " · " : ""}measured ${g.mm}mm (~${g.wear}% worn)`;
    }
    try {
      const r = await api.post("/vhc/ai-explain", {
        item_name: itemLabel,
        urgency: g.grade,
        tech_note: contextNote,
        vehicle: vehicle || "",
      });
      const text = (r.data?.description || "").trim();
      if (text) {
        setField(key, { note: text, aiBusy: false });
        toast.success("Rewrote in customer-friendly wording.");
      } else {
        setField(key, { aiBusy: false });
        toast.info("AI couldn't rewrite that — leave your own note.");
      }
    } catch (err) {
      setField(key, { aiBusy: false });
      toast.error(err?.response?.data?.detail || "AI rewrite failed — try again.");
    }
  };

  const save = async () => {
    const payload = [];
    CATEGORIES.forEach((cat) => {
      cat.items.forEach((it) => {
        const g = grades[it.key];
        if (!g?.grade || g.grade === "green") return;
        const price = parseFloat(g.price || "0") || 0;
        const measurementNote = g.mm && g.wear != null
          ? ` (Measured ${g.mm}mm, ~${g.wear}% worn)`
          : "";
        payload.push({
          type: "labor",
          name: it.label,
          description: (g.note?.trim() ||
            (g.grade === "red"
              ? "Requires attention — noted on VHC by the technician."
              : "Advisory — worth monitoring on the next visit.")) + measurementNote,
          quantity: 1,
          unit_price: price,
          urgency: g.grade,
          photo_urls: (g.photos || []).map((p) => p.path),
        });
      });
    });

    if (!payload.length) {
      toast.info("Mark at least one item Advisory or Requires attention to add work to the quote.");
      return;
    }

    setSaving(true);
    try {
      const r = await api.post(`/jobs/${jobId}/items/bulk`, payload);
      toast.success(`VHC saved — ${r.data.added} item${r.data.added === 1 ? "" : "s"} added to the quote.`);
      onSaved?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the VHC — try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-6" data-testid="vhc-checklist-modal">
      <div className="w-full max-w-3xl bg-card border border-border rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="border-b border-border px-5 py-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary grid place-items-center shrink-0">
            <ScanLine className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline text-primary">Digital VHC · traffic-light checklist</div>
            <div className="font-display font-bold text-lg leading-tight mt-0.5">Vehicle Health Check</div>
            <div className="flex gap-3 mt-2 text-xs flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-success" /> {summary.g} good</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-warning" /> {summary.a} advisory</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-destructive" /> {summary.r} requires attention</span>
              <span className="text-muted-foreground">· {summary.done}/{totalItems} graded</span>
            </div>
          </div>
          <button type="button" onClick={close} className="p-2 hover:bg-muted rounded-md" data-testid="vhc-checklist-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {CATEGORIES.map((cat) => {
            const isOpen = !!expanded[cat.name];
            const catCounts = cat.items.reduce((acc, it) => {
              const g = grades[it.key]?.grade;
              if (g === "green") acc.g++; else if (g === "amber") acc.a++; else if (g === "red") acc.r++;
              return acc;
            }, { g: 0, a: 0, r: 0 });
            const catKey = cat.name.toLowerCase().replace(/[^a-z]+/g, "-");
            return (
              <div key={cat.name} className="border border-border rounded-lg overflow-hidden" data-testid={`vhc-cat-${catKey}`}>
                <button
                  type="button"
                  onClick={() => toggleCategory(cat.name)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors"
                  data-testid={`vhc-cat-toggle-${catKey}`}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                  <div className="font-display font-bold text-sm tracking-tight flex-1 text-left">{cat.name}</div>
                  <div className="flex gap-1.5 text-[10px]">
                    {catCounts.r > 0 && <span className="px-1.5 py-0.5 rounded bg-destructive/15 text-destructive font-bold">{catCounts.r}</span>}
                    {catCounts.a > 0 && <span className="px-1.5 py-0.5 rounded bg-warning/15 text-warning font-bold">{catCounts.a}</span>}
                    {catCounts.g > 0 && <span className="px-1.5 py-0.5 rounded bg-success/15 text-success font-bold">{catCounts.g}</span>}
                    <span className="px-1.5 py-0.5 text-muted-foreground">{catCounts.g + catCounts.a + catCounts.r}/{cat.items.length}</span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-border p-3 space-y-2 bg-background/40">
                    {cat.items.map((it) => {
                      const g = grades[it.key] || {};
                      const isFlagged = g.grade === "amber" || g.grade === "red";
                      const measureKind = it.measure;
                      const measureLabel = measureKind === "tyre" ? "Tread mm" : "Pad mm";
                      return (
                        <div key={it.key} className="border border-border rounded-lg p-3 bg-card" data-testid={`vhc-row-${it.key}`}>
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex-1 min-w-[180px] text-sm font-medium">{it.label}</div>
                            {measureKind && (
                              <div className="flex items-center gap-2">
                                <div className="relative">
                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    min="0"
                                    step="0.1"
                                    value={g.mm ?? ""}
                                    onChange={(e) => onMeasurementChange(it.key, measureKind, e.target.value)}
                                    placeholder={measureLabel}
                                    className="input-default text-xs h-8 w-24 pr-6"
                                    data-testid={`vhc-mm-${it.key}`}
                                    aria-label={measureLabel}
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">mm</span>
                                </div>
                                {g.wear != null && (
                                  <span
                                    className={`text-[10px] font-bold px-1.5 py-1 rounded ${
                                      g.wear >= 80 ? "bg-destructive/15 text-destructive"
                                      : g.wear >= 50 ? "bg-warning/15 text-warning"
                                      : "bg-success/15 text-success"
                                    }`}
                                    data-testid={`vhc-wear-${it.key}`}
                                  >
                                    {g.wear}% worn
                                  </span>
                                )}
                              </div>
                            )}
                            <div className="inline-flex gap-1.5">
                              {GRADES.map((grade) => (
                                <button
                                  key={grade.v}
                                  type="button"
                                  onClick={() => manualSetGrade(it.key, grade.v)}
                                  className={`px-2.5 h-9 rounded-md border text-[11px] font-bold uppercase tracking-wider transition-all ${
                                    g.grade === grade.v
                                      ? `${grade.active} ring-2`
                                      : "bg-muted/40 text-muted-foreground border-border hover:bg-muted"
                                  }`}
                                  data-testid={`vhc-${it.key}-${grade.v}`}
                                  aria-label={`Mark ${it.label} ${grade.short}`}
                                >
                                  {grade.short}
                                </button>
                              ))}
                            </div>
                          </div>

                          {isFlagged && (
                            <div className="mt-3 space-y-2">
                              <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-2">
                                <div className="relative">
                                  <textarea
                                    rows={2}
                                    value={g.note || ""}
                                    onChange={(e) => setField(it.key, { note: e.target.value })}
                                    placeholder={g.grade === "red"
                                      ? "What did you find? (e.g. NSF pad down to 2.5mm)"
                                      : "Advisory note (e.g. pads at 4mm — monitor)"}
                                    className="input-default text-sm w-full resize-none pr-9"
                                    data-testid={`vhc-note-${it.key}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => aiWrite(it.key, it.label)}
                                    disabled={g.aiBusy}
                                    title="Rewrite as customer-friendly wording"
                                    className="absolute top-1.5 right-1.5 p-1.5 rounded-md bg-primary/15 text-primary hover:bg-primary/25 disabled:opacity-50"
                                    data-testid={`vhc-ai-${it.key}`}
                                  >
                                    {g.aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                  </button>
                                </div>
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={g.price || ""}
                                    onChange={(e) => setField(it.key, { price: e.target.value })}
                                    placeholder="0.00"
                                    className="input-default text-sm pl-6 w-full"
                                    data-testid={`vhc-price-${it.key}`}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => openCamera(it.key)}
                                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-border bg-background/40 hover:bg-muted"
                                  data-testid={`vhc-camera-${it.key}`}
                                >
                                  <Camera className="w-3.5 h-3.5" />
                                  {(g.photos || []).length ? "Add another" : "Add photo"}
                                </button>
                                <input
                                  ref={(el) => { fileRefs.current[it.key] = el; }}
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => onPhotoChosen(it.key, e)}
                                  data-testid={`vhc-file-${it.key}`}
                                />
                                {(g.photos || []).map((p, idx) => (
                                  <div key={idx} className="relative w-14 h-14 rounded-md overflow-hidden border border-border group" data-testid={`vhc-thumb-${it.key}-${idx}`}>
                                    <img src={p.url} alt="" className="w-full h-full object-cover" />
                                    <button
                                      type="button"
                                      onClick={() => removePhoto(it.key, idx)}
                                      className="absolute top-0.5 right-0.5 p-0.5 rounded bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity"
                                      aria-label="Remove photo"
                                    >
                                      <Trash2 className="w-3 h-3 text-destructive" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-border px-5 py-3 flex items-center gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground flex-1 min-w-0">
            Only Advisory &amp; Requires-attention items are added to the customer quote. Good items stay off the invoice.
          </div>
          <button type="button" onClick={close} className="btn-outline text-sm" data-testid="vhc-checklist-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || (summary.a + summary.r) === 0}
            className="btn-primary text-sm inline-flex items-center gap-2"
            data-testid="vhc-checklist-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Add {summary.a + summary.r} item{(summary.a + summary.r) === 1 ? "" : "s"} to quote
          </button>
        </div>
      </div>
    </div>
  );
}
